import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Check, AlertTriangle, Loader2, Lock, RotateCcw, ChevronRight, CalendarPlus, X, Pencil } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { RequestTimeChangeModal, DayRequest } from './RequestTimeChangeModal';
import { AddMissedDayModal } from './AddMissedDayModal';

// Employee self-service "My Time" page. Shows the employee's own hours for the
// current week + the 2 prior completed pay periods (Fri→Thu), lets them request
// DAY-LEVEL clock-in/out changes on closed days, and approve last week during the
// Fri→Mon-8am window. Large fonts for readability in direct sun.
//
// Live for every role since 2026-10-30 (was super-admin-only while testing).
// Reached from the "My Time" tile on each role dashboard.

const pad = (n: number) => String(n).padStart(2, '0');
const localDateStr = (input: string | Date) => {
  const d = typeof input === 'string' ? new Date(input) : input;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const fmtTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—';

const formatHM = (hours: number): string => {
  const total = Math.round((Number(hours) || 0) * 60);
  return `${Math.floor(total / 60)}:${pad(total % 60)}`;
};

// Friday→Thursday week, offsetWeeks relative to the current week
// (0 = current week, -1 = last completed pay period, -2 = the one before).
const getWeekPeriod = (offsetWeeks: number) => {
  const today = new Date();
  const currentDay = today.getDay();
  let daysSinceLastFriday: number;
  if (currentDay === 0) daysSinceLastFriday = 2;
  else if (currentDay >= 1 && currentDay <= 4) daysSinceLastFriday = currentDay + 2;
  else daysSinceLastFriday = currentDay - 5;

  const friday = new Date(today);
  friday.setDate(today.getDate() - daysSinceLastFriday + offsetWeeks * 7);
  friday.setHours(0, 0, 0, 0);
  const thursday = new Date(friday);
  thursday.setDate(friday.getDate() + 6);
  thursday.setHours(23, 59, 59, 999);
  return { friday, thursday, fridayStr: localDateStr(friday) };
};

// Last week can be self-approved only during the Fri 00:00 → Mon 08:00 window
// (after that payroll is locked and the button disappears).
const inApprovalWindowNow = () => {
  const now = new Date();
  const curFriday = getWeekPeriod(0).friday;            // this week's Friday 00:00
  const windowEnd = new Date(curFriday);
  windowEnd.setDate(curFriday.getDate() + 3);           // Monday
  windowEnd.setHours(8, 0, 0, 0);                       // Monday 08:00
  return now.getTime() >= curFriday.getTime() && now.getTime() < windowEnd.getTime();
};

const txt = {
  en: {
    heading: 'My Time',
    thisWeek: 'This week', lastWeek: 'Last week', twoWeeks: '2 weeks ago',
    total: 'Total', inProgress: 'in progress', requestChange: 'Request a change',
    pending: 'Change requested · pending', approved: 'Approved',
    changesRequested: 'Changes requested', changesReviewed: 'Changes reviewed', notReviewed: 'Not yet reviewed',
    approveBtn: 'Approve my time', approving: 'Approving…',
    approveHint: 'Confirms all your hours for this pay period are correct.',
    youApproved: 'You approved', officeApproved: 'Approved by office', locked: 'Locked by office',
    addMissed: 'Add a missed day', missedPending: 'Missed day requested · pending',
    editRequest: 'Edit request', withdraw: 'Withdraw',
    sentToQb: 'Sent to QuickBooks · closed',
    changePending: 'Change requested · pending', changeApproved: 'Change approved', changeDenied: 'Change denied',
    missedApproved: 'Missed day added', missedDenied: 'Missed day denied', adminNote: 'Manager note',
    viewOnly: 'View only', editWindow: 'Open until Mon 8 AM',
    lockedHint: 'Changes to last week are only allowed until Monday 8 AM.',
    noTime: 'No time this week', noTimeSub: "You didn't clock in during this period.",
    adminsOnly: 'Restricted', adminsOnlyMsg: 'This page is in testing and limited to super admins.',
    back: 'Back to dashboard', loading: 'Loading your time…',
    retry: 'Retry', loadFailed: 'Failed to load your time.', approveFailed: 'Failed to approve.',
    warn: 'This year', requests: 'time-change requests', forgot: 'forgot to clock out',
  },
  es: {
    heading: 'Mi Tiempo',
    thisWeek: 'Esta semana', lastWeek: 'Semana pasada', twoWeeks: 'Hace 2 semanas',
    total: 'Total', inProgress: 'en progreso', requestChange: 'Solicitar cambio',
    pending: 'Cambio solicitado · pendiente', approved: 'Aprobado',
    changesRequested: 'Cambios solicitados', changesReviewed: 'Cambios revisados', notReviewed: 'Sin revisar',
    approveBtn: 'Aprobar mi tiempo', approving: 'Aprobando…',
    approveHint: 'Confirma que todas sus horas de este período son correctas.',
    youApproved: 'Usted aprobó', officeApproved: 'Aprobado por oficina', locked: 'Bloqueado por oficina',
    addMissed: 'Agregar día faltante', missedPending: 'Día faltante solicitado · pendiente',
    editRequest: 'Editar solicitud', withdraw: 'Retirar',
    sentToQb: 'Enviado a QuickBooks · cerrado',
    changePending: 'Cambio solicitado · pendiente', changeApproved: 'Cambio aprobado', changeDenied: 'Cambio denegado',
    missedApproved: 'Día faltante agregado', missedDenied: 'Día faltante denegado', adminNote: 'Nota del gerente',
    viewOnly: 'Solo lectura', editWindow: 'Abierto hasta lun 8 AM',
    lockedHint: 'Los cambios a la semana pasada solo se permiten hasta el lunes a las 8 AM.',
    noTime: 'Sin tiempo esta semana', noTimeSub: 'No registró entrada en este período.',
    adminsOnly: 'Restringido', adminsOnlyMsg: 'Esta página está en prueba y limitada a super admins.',
    back: 'Volver al panel', loading: 'Cargando su tiempo…',
    retry: 'Reintentar', loadFailed: 'No se pudo cargar su tiempo.', approveFailed: 'No se pudo aprobar.',
    warn: 'Este año', requests: 'solicitudes de cambio', forgot: 'olvidó marcar salida',
  },
};

export const MyTime: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  // Reactive language — follows the app's global ES/EN toggle (Header dispatches a
  // `languageChange` event + writes localStorage; see LanguageContext).
  const [language, setLanguage] = useState<'en' | 'es'>(() => (localStorage.getItem('language') as 'en' | 'es') || 'en');
  useEffect(() => {
    const onLang = (e: any) => setLanguage(e.detail === 'es' ? 'es' : 'en');
    window.addEventListener('languageChange', onLang as any);
    return () => window.removeEventListener('languageChange', onLang as any);
  }, []);
  const L = txt[language];
  const dloc = language === 'es' ? 'es-US' : 'en-US';
  const fmtVacD = (s: string) => new Date(s + 'T12:00:00').toLocaleDateString(dloc, { month: 'short', day: 'numeric' });

  const [offset, setOffset] = useState(0); // 0 = current, -1 last, -2 two ago
  const { friday, thursday, fridayStr } = useMemo(() => getWeekPeriod(offset), [offset]);

  const [entries, setEntries] = useState<any[]>([]);
  const [corrections, setCorrections] = useState<any[]>([]);
  const [officeApproved, setOfficeApproved] = useState(false);
  const [reviewStatus, setReviewStatus] = useState<string>('pending');
  const [isAsphaltCrew, setIsAsphaltCrew] = useState(false);
  const [jobs, setJobs] = useState<any[]>([]);
  const [stats, setStats] = useState<{ change_requests: number; forgot_clock_out: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [modalDay, setModalDay] = useState<DayRequest | null>(null);
  const [modalPending, setModalPending] = useState<{ clockInIso?: string; clockOutIso?: string; noLunch?: boolean; note?: string; ids: number[] } | null>(null);
  const [showMissed, setShowMissed] = useState(false);
  const [editMissed, setEditMissed] = useState<any | null>(null);
  const [weekVacation, setWeekVacation] = useState<{ start_date: string; end_date: string; hours_requested: number } | null>(null);

  // One-time: asphalt-crew flag + year-to-date stats + jobs (for missed-day requests)
  useEffect(() => {
    api.get(API_CONFIG.ENDPOINTS.TIMECARD_IS_ASPHALT_CREW)
      .then((r) => { if (r.data?.success) setIsAsphaltCrew(!!r.data.data?.isAsphaltCrew); })
      .catch(() => {});
    api.get(API_CONFIG.ENDPOINTS.TIMECARD_MY_STATS)
      .then((r) => { if (r.data?.success) setStats(r.data.data); })
      .catch(() => {});
    api.get(API_CONFIG.ENDPOINTS.JOBS)
      .then((r) => { if (r.data?.success) setJobs(r.data.data || []); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadWeek = async () => {
    setLoading(true);
    setError(null);
    try {
      const [entriesRes, statusRes] = await Promise.all([
        api.get(`${API_CONFIG.ENDPOINTS.TIMECARD_REVIEW_ENTRIES}?week_start=${fridayStr}`),
        api.get(`${API_CONFIG.ENDPOINTS.TIMECARD_REVIEW_STATUS}?week_start=${fridayStr}`),
      ]);
      if (entriesRes.data?.success) {
        setEntries(entriesRes.data.data?.entries || []);
        setCorrections(entriesRes.data.data?.corrections || []);
        setOfficeApproved(!!entriesRes.data.data?.office_approved);
      }
      if (statusRes.data?.success) setReviewStatus(statusRes.data.data?.review_status || 'pending');

      // Vacation requested that overlaps the displayed week (gold banner)
      try {
        const empId = auth?.user?.employee_id;
        if (empId) {
          const vres = await api.get(`/api/vacation/public/schedule/${empId}`);
          const list = vres.data?.success ? (vres.data.data || []) : [];
          const thuStr = localDateStr(thursday);
          const hit = list.find((v: any) => v.start_date <= thuStr && v.end_date >= fridayStr);
          setWeekVacation(hit ? { start_date: hit.start_date, end_date: hit.end_date, hours_requested: hit.hours_requested } : null);
        }
      } catch { setWeekVacation(null); }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || L.loadFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWeek();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fridayStr]);

  // Refresh on resume (installed PWAs often skip visibilitychange — also listen on focus).
  useEffect(() => {
    const refresh = () => { if (document.visibilityState !== 'hidden') loadWeek().catch(() => {}); };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fridayStr]);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(friday);
      d.setDate(friday.getDate() + i);
      const key = localDateStr(d);
      const dayEntries = entries
        .filter((e) => localDateStr(e.clock_in) === key)
        .sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime());
      return { date: d, key, entries: dayEntries };
    });
  }, [friday, entries]);

  const totalHours = useMemo(
    () => entries.reduce((s, e) => s + (Number(e.hours_worked) || 0), 0),
    [entries],
  );

  // Derive, from the week's corrections, the per-day change status (entry-linked)
  // and the missed-day rows + their outcomes.
  const corr = useMemo(() => {
    const entryDay = new Map<number, string>();
    entries.forEach((e) => entryDay.set(e.entry_id, localDateStr(e.clock_in)));
    const dayStatus: Record<string, { status: string; note?: string }> = {};
    // Per-day PENDING request details, for the edit/withdraw flow.
    const dayPending: Record<string, { ids: number[]; clockInIso?: string; clockOutIso?: string; noLunch?: boolean; note?: string }> = {};
    const approvedMissed = new Set<string>();
    const pendingMissedKeys = new Set<string>();
    const missedRows: any[] = []; // pending or rejected (approved ones become real entries)
    const rank: Record<string, number> = { pending: 3, rejected: 2, approved: 1 };
    // 'modified' = admin approved with tweaks → treat as approved for display.
    const norm = (s: string) => (s === 'modified' ? 'approved' : s);
    for (const c of corrections) {
      const st = norm(c.admin_status);
      if (c.correction_type === 'missed_day') {
        const dk = String(c.proposed_date || '').split('T')[0];
        if (st === 'approved') approvedMissed.add(dk);
        else { if (st === 'pending') pendingMissedKeys.add(dk); missedRows.push(c); }
        continue;
      }
      const dk = c.entry_id != null ? entryDay.get(c.entry_id) : undefined;
      if (!dk) continue;
      const prev = dayStatus[dk];
      if (!prev || rank[st] > rank[prev.status]) dayStatus[dk] = { status: st, note: c.admin_notes };
      if (st === 'pending') {
        const p = dayPending[dk] || { ids: [] };
        p.ids.push(c.correction_id);
        if (c.correction_type === 'wrong_clock_in') p.clockInIso = c.proposed_clock_in;
        if (c.correction_type === 'wrong_clock_out') p.clockOutIso = c.proposed_clock_out;
        if (c.correction_type === 'did_not_take_lunch') p.noLunch = true;
        if (!p.note && c.employee_notes) p.note = c.employee_notes;
        dayPending[dk] = p;
      }
    }
    return { dayStatus, dayPending, approvedMissed, pendingMissedKeys, missedRows };
  }, [entries, corrections]);

  // Days in this week with NO entry, not in the future, and not already pending
  // → valid "missed day" picks.
  const missingDays = useMemo(() => {
    const todayKey = localDateStr(new Date());
    return days
      .filter((d) => d.entries.length === 0 && d.key <= todayKey && !corr.pendingMissedKeys.has(d.key))
      .map((d) => ({ value: d.key, label: d.date.toLocaleDateString(language === 'es' ? 'es-US' : 'en-US', { weekday: 'long', month: 'short', day: 'numeric' }) }));
  }, [days, language, corr]);

  const selfApproved = reviewStatus === 'approved';
  const isApproved = selfApproved || officeApproved; // either locks editing
  const inWindow = inApprovalWindowNow();
  const hasPendingChanges = corrections.some((c) => c.admin_status === 'pending');
  // Submitting corrections WAS the employee's review — once the admin has
  // settled every one ('resolved', or legacy 'issues_flagged' with none left
  // pending), the week counts as reviewed and we never ask them to approve
  // again. New change requests stay allowed (canRequest) in case something
  // else was wrong.
  const correctionsSettled = corrections.length > 0 && !hasPendingChanges;
  const reviewComplete = reviewStatus === 'resolved' || (reviewStatus === 'issues_flagged' && correctionsSettled);
  // Week badge is derived from ACTUAL data, not the stale EMPLOYEE_TIMECARD_REVIEWS
  // flag. Precedence: office sign-off > self-approval > reviewed-via-corrections
  // > pending changes > clean.
  const badgeStatus = officeApproved ? 'office_approved'
    : selfApproved ? 'approved'
    : reviewComplete ? 'resolved'
    : hasPendingChanges ? 'issues_flagged'
    : 'pending';
  // Change requests + missed-day adds: ALWAYS allowed on the current week (in
  // progress); on LAST week only during the Fri→Mon 8am window. 2-weeks-ago is
  // locked (view-only). Anything office-approved or self-approved is locked.
  const canRequest = !isApproved && (offset === 0 || (offset === -1 && inWindow));
  const showApprove = offset === -1 && entries.length > 0 && !officeApproved;
  const canApprove = showApprove && !isApproved && !reviewComplete && inWindow;

  const approve = async () => {
    setApproving(true);
    setError(null);
    try {
      const res = await api.post(API_CONFIG.ENDPOINTS.TIMECARD_REVIEW_APPROVE, { week_start_date: fridayStr });
      if (!res.data?.success) throw new Error(res.data?.error || L.approveFailed);
      setReviewStatus('approved');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || L.approveFailed);
    } finally {
      setApproving(false);
    }
  };

  const onSubmitted = async () => {
    setModalDay(null);
    setModalPending(null);
    setShowMissed(false);
    setEditMissed(null);
    await loadWeek();
    api.get(API_CONFIG.ENDPOINTS.TIMECARD_MY_STATS)
      .then((r) => { if (r.data?.success) setStats(r.data.data); })
      .catch(() => {});
  };

  // Build a day-level change request (collapse the day to its clock-in/clock-out).
  // When the day already has a pending request, also load it for editing.
  const openRequest = (day: { date: Date; key: string; entries: any[] }) => {
    const firstE = day.entries[0];
    const lastE = day.entries[day.entries.length - 1];
    if (!firstE || !lastE?.clock_out) return;
    const jobNames = Array.from(new Set(day.entries.map((e) => e.job_name).filter(Boolean)));
    setModalDay({
      dateLabel: day.date.toLocaleDateString(dloc, { weekday: 'short', month: 'short', day: 'numeric' }),
      entryDate: (firstE.entry_date ? String(firstE.entry_date).split('T')[0] : localDateStr(firstE.clock_in)),
      jobName: jobNames.join(', '),
      clockInEntryId: firstE.entry_id,
      clockInIso: firstE.clock_in,
      clockOutEntryId: lastE.entry_id,
      clockOutIso: lastE.clock_out,
      lunchTaken: day.entries.some((e) => e.lunch_taken),
    });
    setModalPending(corr.dayPending[day.key] || null);
  };

  const spin = <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}.spin{animation:spin 1s linear infinite}`}</style>;

  const weekLabel = `${friday.toLocaleDateString(dloc, { month: 'short', day: 'numeric' })} – ${thursday.toLocaleDateString(dloc, { month: 'short', day: 'numeric' })}`;
  const offenderTotal = (stats?.change_requests || 0) + (stats?.forgot_clock_out || 0);
  const warnColor = offenderTotal >= 5 ? '#dc2626' : offenderTotal >= 3 ? '#d97706' : '#64748b';
  const warnBg = offenderTotal >= 5 ? '#fef2f2' : offenderTotal >= 3 ? '#fffbeb' : '#f1f5f9';

  const tabs: Array<{ off: number; label: string }> = [
    { off: 0, label: L.thisWeek }, { off: -1, label: L.lastWeek }, { off: -2, label: L.twoWeeks },
  ];

  return (
    <div style={{ background: '#f1f5f9', minHeight: '100vh', paddingBottom: 40 }}>
      {spin}

      {/* Sticky header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1f2937' }}>{L.heading}</div>
            <div style={{ fontSize: 15, color: '#64748b', fontWeight: 600 }}>{weekLabel}</div>
          </div>
          <button onClick={() => loadWeek()} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }} title="Refresh">
            <RotateCcw size={22} color="#94a3b8" />
          </button>
        </div>
        {/* Week tabs */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          {tabs.map((tb) => (
            <button
              key={tb.off}
              onClick={() => setOffset(tb.off)}
              style={{
                flex: 1, padding: '12px 6px', borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: 'pointer',
                border: offset === tb.off ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                background: offset === tb.off ? '#eef2ff' : '#fff',
                color: offset === tb.off ? '#4338ca' : '#64748b',
              }}
            >
              {tb.label}
            </button>
          ))}
        </div>
      </div>

      {/* Year-to-date warning */}
      {stats && offenderTotal > 0 && (
        <div style={{ margin: '12px 16px 0', display: 'flex', gap: 10, background: warnBg, border: `1px solid ${warnColor}40`, borderRadius: 12, padding: '12px 14px' }}>
          <AlertTriangle size={20} color={warnColor} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 15, color: warnColor, lineHeight: 1.4, fontWeight: 600 }}>
            <strong>{L.warn}:</strong> {stats.change_requests} {L.requests} · {stats.forgot_clock_out} {L.forgot}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 60, color: '#64748b' }}>
          <Loader2 size={32} className="spin" />
          <div style={{ marginTop: 12, fontSize: 16 }}>{L.loading}</div>
        </div>
      ) : error ? (
        <div style={{ margin: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 16, color: '#dc2626', fontSize: 16 }}>
          {error}
          <button onClick={loadWeek} style={{ display: 'block', marginTop: 10, padding: '10px 16px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 16 }}>{L.retry}</button>
        </div>
      ) : (
        <div style={{ padding: '12px 16px 0' }}>
          <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            {/* Summary header */}
            <div style={{ padding: 18, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                <StatusBadge status={badgeStatus} L={L} />
                {offset !== 0 && !isApproved && (
                  (offset === -1 && inWindow) ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#eef2ff', color: '#4338ca', borderRadius: 16, padding: '5px 12px', fontSize: 14, fontWeight: 800 }}>
                      <Clock size={14} /> {L.editWindow}
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f1f5f9', color: '#64748b', borderRadius: 16, padding: '5px 12px', fontSize: 14, fontWeight: 800 }}>
                      <Lock size={13} /> {L.viewOnly}
                    </span>
                  )
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#1f2937', lineHeight: 1 }}>{formatHM(totalHours)}</div>
                <div style={{ fontSize: 14, color: '#64748b', fontWeight: 600, marginTop: 2 }}>{L.total} · h:mm</div>
              </div>
            </div>

            {/* Vacation requested banner (only when the displayed week has vacation) */}
            {weekVacation && (
              <div style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>🌴</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>{language === 'es' ? 'Vacaciones solicitadas' : 'Vacation Requested'}</div>
                  <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: 13 }}>{fmtVacD(weekVacation.start_date)} – {fmtVacD(weekVacation.end_date)}</div>
                </div>
                <span style={{ color: '#fff', fontWeight: 800, fontSize: 18, background: 'rgba(0,0,0,0.18)', padding: '4px 12px', borderRadius: 8 }}>{weekVacation.hours_requested} {language === 'es' ? 'h' : 'hrs'}</span>
              </div>
            )}

            {entries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 50, color: '#64748b' }}>
                <Clock size={40} color="#94a3b8" style={{ marginBottom: 10 }} />
                <div style={{ fontWeight: 700, color: '#1f2937', fontSize: 18 }}>{L.noTime}</div>
                <div style={{ fontSize: 15 }}>{L.noTimeSub}</div>
              </div>
            ) : (
              <div style={{ padding: '4px 0' }}>
                {days.filter((d) => d.entries.length > 0).map((day) => {
                  const lastE = day.entries[day.entries.length - 1];
                  const ds = corr.dayStatus[day.key];
                  const dayClosed = day.entries.every((e) => !!e.clock_out);
                  // Hours already exported to QuickBooks: the pay week is closed, no change requests
                  const dayInQb = day.entries.some((e: any) => !!e.qb_synced);
                  const dayHours = day.entries.reduce((s, e) => s + (Number(e.hours_worked) || 0), 0);
                  return (
                    <div key={day.key} style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {day.date.toLocaleDateString(dloc, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </div>
                        {dayClosed && <div style={{ fontSize: 17, fontWeight: 800, color: '#1f2937' }}>{formatHM(dayHours)}</div>}
                      </div>
                      {day.entries.map((e) => {
                        const isOpen = !e.clock_out;
                        return (
                          <div key={e.entry_id} style={{ background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
                            <div style={{ fontSize: 19, fontWeight: 700, color: '#1f2937' }}>
                              {fmtTime(e.clock_in)} – {isOpen ? <span style={{ color: '#ea580c' }}>{L.inProgress}</span> : fmtTime(e.clock_out)}
                            </div>
                            <div style={{ fontSize: 16, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {e.job_name || `Job #${e.job_id}`}{e.lunch_taken ? ` · lunch ${e.lunch_minutes ?? 30}m` : ''}
                            </div>
                          </div>
                        );
                      })}

                      {/* Day-level change status + action */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        {ds?.status === 'pending' && <Chip bg="#fef3c7" color="#92400e" icon={<AlertTriangle size={15} />} text={L.changePending} />}
                        {ds?.status === 'approved' && <Chip bg="#dcfce7" color="#166534" icon={<Check size={15} />} text={L.changeApproved} />}
                        {ds?.status === 'rejected' && <Chip bg="#fee2e2" color="#991b1b" icon={<X size={15} />} text={L.changeDenied} />}
                        {corr.approvedMissed.has(day.key) && <Chip bg="#dcfce7" color="#166534" icon={<CalendarPlus size={15} />} text={L.missedApproved} />}
                        {/* Pending → editable; approved/denied → locked (no button); none → request */}
                        {dayInQb && <Chip bg="#e5e7eb" color="#374151" icon={<Lock size={15} />} text={L.sentToQb} />}
                        {canRequest && dayClosed && !dayInQb && !!lastE?.clock_out && (
                          ds?.status === 'pending' ? (
                            <button
                              onClick={() => openRequest(day)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #c7d2fe', color: '#4338ca', fontSize: 16, fontWeight: 800, cursor: 'pointer', padding: '10px 16px', borderRadius: 10 }}
                            >
                              <Pencil size={16} /> {L.editRequest}
                            </button>
                          ) : !ds ? (
                            <button
                              onClick={() => openRequest(day)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eef2ff', border: '1px solid #c7d2fe', color: '#4338ca', fontSize: 16, fontWeight: 800, cursor: 'pointer', padding: '10px 16px', borderRadius: 10 }}
                            >
                              {L.requestChange} <ChevronRight size={18} />
                            </button>
                          ) : null
                        )}
                      </div>
                      {ds?.status === 'rejected' && ds.note && (
                        <div style={{ marginTop: 6, fontSize: 14, color: '#991b1b' }}><strong>{L.adminNote}:</strong> {ds.note}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Missed-day requests with no entry yet (pending) or denied — shown separately */}
            {corr.missedRows.length > 0 && (
              <div style={{ padding: '4px 0' }}>
                {corr.missedRows.map((m) => {
                  const dk = String(m.proposed_date || '').split('T')[0];
                  const label = dk ? new Date(dk + 'T12:00:00').toLocaleDateString(dloc, { weekday: 'short', month: 'short', day: 'numeric' }) : '';
                  const denied = m.admin_status === 'rejected';
                  return (
                    <div key={m.correction_id} style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{label}</div>
                      <div style={{ background: denied ? '#fff5f5' : '#fffdf5', border: `1px dashed ${denied ? '#fca5a5' : '#fcd34d'}`, borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ fontSize: 19, fontWeight: 700, color: '#1f2937' }}>{fmtTime(m.proposed_clock_in)} – {fmtTime(m.proposed_clock_out)}</div>
                        {m.proposed_job_name && <div style={{ fontSize: 16, color: '#64748b', marginTop: 2 }}>{m.proposed_job_name}</div>}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
                          {denied
                            ? <Chip bg="#fee2e2" color="#991b1b" icon={<X size={15} />} text={L.missedDenied} />
                            : <Chip bg="#fef3c7" color="#92400e" icon={<AlertTriangle size={15} />} text={L.missedPending} />}
                          {/* Pending missed-day → editable (modal has withdraw); denied → locked */}
                          {!denied && canRequest && (
                            <button
                              onClick={() => setEditMissed({
                                correctionId: m.correction_id,
                                date: dk,
                                dateLabel: label,
                                jobId: m.proposed_job_id ?? null,
                                clockInIso: m.proposed_clock_in,
                                clockOutIso: m.proposed_clock_out,
                                note: m.employee_notes,
                              })}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #c7d2fe', color: '#4338ca', fontSize: 15, fontWeight: 800, cursor: 'pointer', padding: '8px 14px', borderRadius: 10 }}
                            >
                              <Pencil size={15} /> {L.editRequest}
                            </button>
                          )}
                        </div>
                        {denied && m.admin_notes && (
                          <div style={{ marginTop: 6, fontSize: 14, color: '#991b1b' }}><strong>{L.adminNote}:</strong> {m.admin_notes}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add a missed day (forgot to clock in) */}
            {canRequest && missingDays.length > 0 && (
              <div style={{ padding: 16 }}>
                <button
                  onClick={() => setShowMissed(true)}
                  style={{ width: '100%', padding: 16, borderRadius: 12, border: '2px dashed #c7d2fe', background: '#fff', color: '#4338ca', fontSize: 17, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  <CalendarPlus size={20} /> {L.addMissed}
                </button>
              </div>
            )}
          </div>

          {/* Approve footer — last week only, Fri→Mon 8am window */}
          {showApprove && (isApproved || canApprove) && (
            <div style={{ padding: '16px 0' }}>
              {isApproved ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#dcfce7', color: '#166534', borderRadius: 12, padding: 18, fontSize: 18, fontWeight: 800 }}>
                  <Check size={20} /> {L.youApproved}
                </div>
              ) : (
                <>
                  <button onClick={approve} disabled={approving}
                    style={{ width: '100%', padding: 18, borderRadius: 14, border: 'none', background: '#16a34a', color: '#fff', fontSize: 20, fontWeight: 800, cursor: approving ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: approving ? 0.7 : 1 }}>
                    {approving ? <Loader2 size={20} className="spin" /> : <Check size={20} />} {approving ? L.approving : L.approveBtn}
                  </button>
                  <div style={{ textAlign: 'center', fontSize: 14, color: '#64748b', marginTop: 8 }}>{L.approveHint}</div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {modalDay && (
        <RequestTimeChangeModal
          day={modalDay}
          weekStartDate={fridayStr}
          isAsphaltCrew={isAsphaltCrew}
          stats={stats}
          language={language}
          pending={modalPending}
          onClose={() => { setModalDay(null); setModalPending(null); }}
          onSubmitted={onSubmitted}
        />
      )}

      {(showMissed || editMissed) && (
        <AddMissedDayModal
          weekStartDate={fridayStr}
          missingDays={missingDays}
          jobs={jobs}
          language={language}
          editing={editMissed}
          onClose={() => { setShowMissed(false); setEditMissed(null); }}
          onSubmitted={onSubmitted}
        />
      )}
    </div>
  );
};

const Chip: React.FC<{ bg: string; color: string; icon: React.ReactNode; text: string; style?: React.CSSProperties }> = ({ bg, color, icon, text, style }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color, borderRadius: 16, padding: '6px 12px', fontSize: 15, fontWeight: 800, ...style }}>
    {icon} {text}
  </span>
);

const StatusBadge: React.FC<{ status: string; L: typeof txt['en'] }> = ({ status, L }) => {
  const map: Record<string, { bg: string; color: string; label: string; icon: React.ReactNode }> = {
    office_approved: { bg: '#dcfce7', color: '#166534', label: L.officeApproved, icon: <Lock size={15} /> },
    approved: { bg: '#dcfce7', color: '#166534', label: L.youApproved, icon: <Check size={16} /> },
    issues_flagged: { bg: '#fef3c7', color: '#92400e', label: L.changesRequested, icon: <AlertTriangle size={16} /> },
    // Backend sets 'resolved' once the admin has handled every correction
    resolved: { bg: '#dbeafe', color: '#1e40af', label: L.changesReviewed, icon: <Check size={16} /> },
  };
  const s = map[status] || { bg: '#f1f5f9', color: '#475569', label: L.notReviewed, icon: <Clock size={16} /> };
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: s.bg, color: s.color, borderRadius: 20, padding: '7px 14px', fontSize: 15, fontWeight: 800 }}>
      {s.icon} {s.label}
    </div>
  );
};

export default MyTime;
